import React, { useMemo, useState } from 'react';
import { Drawer, Button, Stack, Divider } from 'rsuite';
import { useMessenger } from '../../context/messengerContext';
import {
    getMaxIdFromUrl,
    getMaxIdFromStorage,
    getTgIdFromUrl,
    getTgIdFromStorage,
} from '../../context/messengerContext';

import '../navMenu/navMenu.css';
import './debugOverlay.css';

function safeJson(value) {
    try {
        return JSON.stringify(value ?? null);
    } catch (e) {
        return `"${String(e?.message || e)}"`;
    }
}

export default function DebugOverlay() {
    const [open, setOpen] = useState(false);
    const { platform, userId, nativePlatform, initData, initDataUnsafe } =
        useMessenger();

    const snapshot = useMemo(() => {
        const telegramInitData = window?.Telegram?.WebApp?.initData;
        const maxInitData = window?.WebApp?.initData;
        const telegramInitDataUnsafe = window?.Telegram?.WebApp?.initDataUnsafe;

        return {
            timestamp: new Date().toISOString(),
            platform,
            nativePlatform,
            userId,
            window: {
                hasTelegramWebApp: Boolean(window?.Telegram?.WebApp),
                hasTelegramWebviewProxy: Boolean(window?.TelegramWebviewProxy),
                hasMaxWebApp: Boolean(window?.WebApp),
            },
            telegram: {
                initData: telegramInitData,
                initDataUnsafe: telegramInitDataUnsafe,
            },
            max: {
                initData: maxInitData,
            },
            context: {
                initData,
                initDataUnsafe,
            },
            ids: {
                tgIdFromUrl: getTgIdFromUrl(),
                tgIdFromStorage: getTgIdFromStorage(),
                maxIdFromUrl: getMaxIdFromUrl(),
                maxIdFromStorage: getMaxIdFromStorage(),
            },
            location: {
                href: window?.location?.href,
            },
        };
    }, [platform, nativePlatform, userId, initData, initDataUnsafe]);

    const copy = async () => {
        const text = JSON.stringify(snapshot, null, 2);
        try {
            await navigator.clipboard.writeText(text);
        } catch (_) {
            // fallback
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
    };

    return (
        <>
            <button
                type='button'
                className='navMenu_burger debugOverlay_fab'
                onClick={() => setOpen((prev) => !prev)}
                aria-label='Debug'
                title='Debug'
            >
                DBG
            </button>

            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                placement='right'
                size='xs'
                className='navMenu_drawer debugOverlay_drawer'
            >
                <Drawer.Header>
                    <Drawer.Title>Debug</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body className='navMenu_body debugOverlay_body'>
                    <Stack spacing={8} wrap>
                        <Button appearance='primary' onClick={copy}>
                            Copy JSON
                        </Button>
                        <Button
                            appearance='subtle'
                            onClick={() => window.location.reload()}
                        >
                            Reload
                        </Button>
                    </Stack>

                    <Divider />

                    <div className='debugOverlay_grid'>
                        <div className='debugOverlay_row'>
                            <div className='debugOverlay_label'>platform</div>
                            <div className='debugOverlay_value'>
                                <code className='debugOverlay_code'>
                                    {String(snapshot.platform)}
                                </code>
                            </div>
                        </div>
                        <div className='debugOverlay_row'>
                            <div className='debugOverlay_label'>userId</div>
                            <div className='debugOverlay_value'>
                                <code className='debugOverlay_code'>
                                    {String(snapshot.userId)}
                                </code>
                            </div>
                        </div>
                        <div className='debugOverlay_row'>
                            <div className='debugOverlay_label'>
                                window.WebApp?.initData
                            </div>
                            <div className='debugOverlay_value'>
                                <code className='debugOverlay_code'>
                                    {safeJson(snapshot.max.initData)}
                                </code>
                            </div>
                        </div>
                        <div className='debugOverlay_row'>
                            <div className='debugOverlay_label'>
                                window.Telegram?.WebApp?.initData
                            </div>
                            <div className='debugOverlay_value'>
                                <code className='debugOverlay_code'>
                                    {safeJson(snapshot.telegram.initData)}
                                </code>
                            </div>
                        </div>
                        <div className='debugOverlay_row'>
                            <div className='debugOverlay_label'>
                                max_id (url/storage)
                            </div>
                            <div className='debugOverlay_value'>
                                <code className='debugOverlay_code'>
                                    {safeJson({
                                        url: snapshot.ids.maxIdFromUrl,
                                        storage: snapshot.ids.maxIdFromStorage,
                                    })}
                                </code>
                            </div>
                        </div>
                        <div className='debugOverlay_row'>
                            <div className='debugOverlay_label'>
                                tg_id (url/storage)
                            </div>
                            <div className='debugOverlay_value'>
                                <code className='debugOverlay_code'>
                                    {safeJson({
                                        url: snapshot.ids.tgIdFromUrl,
                                        storage: snapshot.ids.tgIdFromStorage,
                                    })}
                                </code>
                            </div>
                        </div>
                    </div>

                    <Divider />

                    <pre className='debugOverlay_pre'>
                        {JSON.stringify(snapshot, null, 2)}
                    </pre>
                </Drawer.Body>
            </Drawer>
        </>
    );
}
